export type Segment = "detractor" | "passive" | "promoter"
export type PageSlug = "overview" | "performance" | "reviews" | "listings"

export interface FeedbackQuestion {
  question: string
  options: string[]
}

export interface PageQuestionSet {
  segmentQuestion: Record<Segment, FeedbackQuestion>
  commonQuestions: FeedbackQuestion[]
}

export const PAGE_DISPLAY_NAMES: Record<PageSlug, string> = {
  overview: "Overview",
  performance: "Performance",
  reviews: "Reviews",
  listings: "Listing Intelligence",
}

export const PAGE_QUESTIONS: Record<PageSlug, PageQuestionSet> = {
  overview: {
    segmentQuestion: {
      detractor: {
        question: "What's not working for you on the Overview?",
        options: [
          "Too much information at once",
          "Metrics don't feel relevant",
          "AI recommendations aren't helpful",
          "Page loads too slowly",
          "Other",
        ],
      },
      passive: {
        question: "What one thing would make the Overview more valuable?",
        options: [
          "More actionable insights",
          "Customizable layout",
          "Better data visualizations",
          "Faster updates",
          "Other",
        ],
      },
      promoter: {
        question: "What do you find most valuable on the Overview?",
        options: [
          "The AI action items",
          "The pulse metrics",
          "The coverage check",
          "The what-changed panel",
          "Other",
        ],
      },
    },
    commonQuestions: [
      {
        question: "How often do you check this page?",
        options: ["Daily", "A few times per week", "Weekly", "Rarely"],
      },
      {
        question: "Which section do you look at first?",
        options: [
          "Pulse cards (revenue, reputation)",
          "Action rail",
          "Coverage check",
          "What changed panel",
          "Commercial intelligence",
        ],
      },
    ],
  },
  performance: {
    segmentQuestion: {
      detractor: {
        question: "What's frustrating about the Performance page?",
        options: [
          "Data doesn't match my PMS",
          "Charts are hard to read",
          "Missing metrics I need",
          "Too slow to load",
          "Other",
        ],
      },
      passive: {
        question: "What would make Performance analytics more useful?",
        options: [
          "Easier comparison periods",
          "Export/download options",
          "More granular breakdowns",
          "Revenue forecasting",
          "Other",
        ],
      },
      promoter: {
        question: "What do you rely on most in Performance?",
        options: [
          "Revenue trends",
          "Occupancy calendar",
          "KPI comparisons",
          "AI insights",
          "Other",
        ],
      },
    },
    commonQuestions: [
      {
        question: "Which time period do you analyze most?",
        options: ["This month", "Year to date", "Next 12 months", "Year over year comparison"],
      },
      {
        question: "Do you use the comparison feature (YoY / previous period)?",
        options: ["Yes, frequently", "Sometimes", "Didn't know it existed", "No, not useful"],
      },
    ],
  },
  reviews: {
    segmentQuestion: {
      detractor: {
        question: "What's missing from the Reviews page?",
        options: [
          "Can't respond to reviews here",
          "Sentiment analysis isn't accurate",
          "Missing a platform I use",
          "Too much information",
          "Other",
        ],
      },
      passive: {
        question: "What would make review tracking more useful?",
        options: [
          "Response drafting tools",
          "Better sentiment detail",
          "Guest loyalty tracking",
          "Competitor review comparison",
          "Other",
        ],
      },
      promoter: {
        question: "What's most valuable about the Reviews page?",
        options: [
          "Cross-platform view",
          "Sentiment trends",
          "Strengths & risks analysis",
          "Category score breakdown",
          "Other",
        ],
      },
    },
    commonQuestions: [
      {
        question: "How many review platforms do you monitor?",
        options: ["Just Airbnb", "Just Booking.com", "Both", "Others not shown here"],
      },
      {
        question: "Do you act on the AI-suggested actions?",
        options: ["Yes, regularly", "Sometimes", "I read them but don't act", "I haven't noticed them"],
      },
    ],
  },
  listings: {
    segmentQuestion: {
      detractor: {
        question: "What's not useful about the Listings page?",
        options: [
          "Audit suggestions aren't relevant",
          "Can't edit listings here",
          "Scores don't reflect reality",
          "Too technical to understand",
          "Other",
        ],
      },
      passive: {
        question: "What would improve the Listing Intelligence page?",
        options: [
          "Direct listing editing",
          "Photo-specific suggestions",
          "SEO keyword recommendations",
          "Competitor listing comparison",
          "Other",
        ],
      },
      promoter: {
        question: "What do you value most about Listing Intelligence?",
        options: [
          "Health score overview",
          "Content suggestions",
          "Photo analysis",
          "Seasonal relevance check",
          "Other",
        ],
      },
    },
    commonQuestions: [
      {
        question: "Have you updated your listing based on the audit?",
        options: ["Yes, multiple times", "Once", "Not yet but plan to", "No"],
      },
      {
        question: "Do you use the AI content generator?",
        options: ["Yes, love it", "Tried it once", "Didn't know it existed", "No, I write my own"],
      },
    ],
  },
}
